import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ShoppingCartContainer } from '@components/organisms/ShoppingCartContainer';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Container } from '@components/atoms/Container';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { formatPrice } from '@utils/format';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import styles from './CardPaymentRedirect.module.css';
import { ServicesCartContainer } from '@components/organisms/ServicesCartContainer';

const CURRENT_STEP = 5;

export const CardPaymentRedirect: FC = () => {
    const location = useLocation();
    const { login, createdOrder, createdReservations } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('card-payment-redirect-component.title'),
        orderNumberLabel: t('card-payment-redirect-component.order-number.label'),
        priceLabel: t('card-payment-redirect-component.price.label'),
        feedbackMessageOrder: t('feedback-component.feedback-message', {
            returnObjects: true,
            name: `${login?.data?.user?.name} ${login?.data?.user?.lastName}`,
        }) as string[],
        feedbackMessageReservation: t('services-booking-feedback-component.feedback-message', {
            returnObjects: true,
            name: `${login?.data?.user?.name} ${login?.data?.user?.lastName}`,
        }) as string[],
        feedbackMessageSuccess: t('card-payment-redirect-component.feedback-message-success'),
        feedbackMessageError: t('card-payment-redirect-component.feedback-message-error'),
        feedbackMessageTryAgain: t('card-payment-redirect-component.feedback-message-try-again'),
        feedbackMessageWaitingCardPayment: t('card-payment-redirect-component.feedback-message-waiting-card-payment'),
        feedbackMessageSentToEmailOrder: t('feedback-component.feedback-message-sent-to-email'),
        feedbackMessageSentToEmailReservation: t('services-booking-feedback-component.feedback-message-sent-to-email-paid'),
        continueShoppingButton: t('feedback-component.continue-shopping.button'),
        returnToMarketplaceButton: t('services-booking-feedback-component.return-to-marketplace.button'),
    };

    const [paymentSuccessful, setPaymentSuccessful] = useState<boolean | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const status = params.get('s');

        if (status === 'ok') {
            setPaymentSuccessful(true);
        } else {
            setPaymentSuccessful(false);
        }
    }, [location.search]);

    const content = (
        <Card classes={styles['order-feedback-card']}>
            <Container classes="text-center" customSize="custom-medium">
                <>
                    <Typography color="success" fontWeight="900" fontSize="36" tagVariant={TypographyTagVariants.span}>
                        <i className="fa-solid fa-badge-check" />
                    </Typography>
                    {createdOrder && (
                        <Typography classes="my-4 py-1" fontWeight="800" fontSize="16">
                            {`${translations.orderNumberLabel}: ${createdOrder?.data?.order_number}`}
                        </Typography>
                    )}
                    <div className="my-4 py-1">
                        {[...(createdOrder ? translations.feedbackMessageOrder : translations.feedbackMessageReservation)].map(
                            (line, index) => (
                                <Typography
                                    key={`line-${index}`}
                                    color="success"
                                    fontWeight="800"
                                    fontSize="20"
                                    breakpointsStyles={{ md: { fontSize: '24' } }}>
                                    {line}
                                </Typography>
                            )
                        )}
                    </div>
                    <hr className="my-4 py-1" />

                    <Typography fontWeight="800" fontSize="20" breakpointsStyles={{ md: { fontSize: '24' } }}>
                        {paymentSuccessful ? translations.feedbackMessageSuccess : translations.feedbackMessageError}
                    </Typography>
                    {!paymentSuccessful && (
                        <Typography classes="my-4 py-1" fontSize="16">
                            <Anchor
                                target="_blank"
                                href={createdOrder ? createdOrder?.data?.payment_url : createdReservations?.data?.payment_url}
                                mode={AnchorMode.none}
                                children={translations.feedbackMessageTryAgain}
                            />
                        </Typography>
                    )}

                    <>
                        {createdOrder ? (
                            <>
                                {createdOrder?.data?.payment_method?.image?.src && (
                                    <div className="my-4 py-1">
                                        <img
                                            src={createdOrder.data.payment_method.image.src}
                                            alt={createdOrder.data.payment_method.name}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {createdReservations?.data?.payment_method?.image?.src && (
                                    <div className="my-4 py-1">
                                        <img
                                            src={createdReservations.data.payment_method.image.src}
                                            alt={createdReservations.data.payment_method.name}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </>

                    <Typography classes="my-4 py-1" fontWeight="800" fontSize="16">
                        <>
                            {`${translations.priceLabel}: `}
                            <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                {formatPrice(createdOrder ? createdOrder?.data?.total : createdReservations?.data?.total)}
                            </Typography>
                        </>
                    </Typography>

                    {paymentSuccessful && (
                        <>
                            <hr className="my-4 py-1" />
                            <Typography fontSize="16">
                                {createdOrder
                                    ? translations.feedbackMessageSentToEmailOrder
                                    : translations.feedbackMessageSentToEmailReservation}
                            </Typography>
                        </>
                    )}
                </>
            </Container>
        </Card>
    );

    return (
        <>
            {createdOrder ? (
                <ShoppingCartContainer
                    step={CURRENT_STEP}
                    title={translations.title}
                    action={
                        <Anchor href={SITE_URLS.products} mode={AnchorMode.primary}>
                            {translations.continueShoppingButton}
                        </Anchor>
                    }>
                    {content}
                </ShoppingCartContainer>
            ) : (
                <ServicesCartContainer
                    step={CURRENT_STEP}
                    title={translations.title}
                    action={
                        <Anchor href={SITE_URLS.home} mode={AnchorMode.primary}>
                            {translations.returnToMarketplaceButton}
                        </Anchor>
                    }
                    displayTotal={false}>
                    {content}
                </ServicesCartContainer>
            )}
        </>
    );
};
